import { useTranslate } from '@tolgee/react';
import { GetServerSideProps } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import styled from 'styled-components';

import LandingPageHome from '../../components/LandingPageHome';
import { NextPageWithLayout } from '../../components/layouts';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../../hooks/useRoutePrefetch';
import { getLocaleProps } from '../../utils/localisationUtils';

interface LocalizedLandingProps {
  locale: string;
}

const LocalizedLanding: NextPageWithLayout<LocalizedLandingProps> = ({ locale }) => {
  const { t } = useTranslate();

  // Prefetch routes for authenticated users
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');

  // Language is set via SSR prop on TolgeeProvider, no need to set it manually

  return (
    <Container>
      <LandingPageLayout
        title={t('meta.landing.title.v2', 'Curipod - Writing Practice Students Love')}
        subTitle={t(
          'meta.landing.description.v6',
          `Teacher led lessons aligned to your district's curriculum. Students read, write and discuss. Not alone on the screen. Together.`,
        )}
        locale={locale}
        hideBlobs={true}
        disableHeadline={true}
      >
        <LandingPageHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

const TIMEOUT = 20000;
export const getServerSideProps: GetServerSideProps = async context => {
  try {
    // Check for authenticated users and redirect them
    const sessionPromise = getSession({ req: context.req });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), TIMEOUT),
    );

    const session = (await Promise.race([
      sessionPromise,
      timeoutPromise,
    ])) as Session | null;

    if (session) {
      return {
        redirect: {
          permanent: false,
          destination: `/`,
        },
      };
    }

    // Set server-side cache headers for performance
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=3600, stale-while-revalidate=86400',
    );
    return getLocaleProps(context);
  } catch (error) {
    return getLocaleProps(context); // This is safe due to internal try catch
  }
};

LocalizedLanding.getLayout = function getLayout(page) {
  return <>{page}</>;
};

export default LocalizedLanding;
